import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerLayout from '../../components/layout/CustomerLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  User, Phone, Mail, MapPin, LogOut, Edit2, Save, Wallet, Gift,
  ChevronRight, Crown, Check, Star, Truck, ArrowUpRight, RefreshCw,
  Shield, FileText, Trash2, AlertTriangle, ArrowLeft, MessageCircle, Headphones,
  Store, UserCog, Repeat
} from 'lucide-react';
import axios from 'axios';
import { TWO_COLUMNS } from '../../components/customer/customerLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLORS = [
  { bg: 'from-amber-400 to-amber-600', text: 'text-amber-900', badge: 'bg-amber-100 text-amber-700', light: 'bg-amber-50' },
  { bg: 'from-sky-400 to-sky-600', text: 'text-sky-900', badge: 'bg-sky-100 text-sky-700', light: 'bg-sky-50' },
  { bg: 'from-purple-400 to-purple-600', text: 'text-purple-900', badge: 'bg-purple-100 text-purple-700', light: 'bg-purple-50' },
  { bg: 'from-emerald-400 to-emerald-600', text: 'text-emerald-900', badge: 'bg-emerald-100 text-emerald-700', light: 'bg-emerald-50' },
];

const CustomerProfile = () => {
  const navigate = useNavigate();
  const { user, logout, updateProfile, storeSettings, availableRoles, switchRole } = useAuth();
  
  const [view, setView] = useState('main');
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [switchingRole, setSwitchingRole] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [membershipData, setMembershipData] = useState(null);
  const [subscribing, setSubscribing] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteOtp, setDeleteOtp] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    address: user?.address ? JSON.stringify(user.address, null, 2) : ''
  });

  useEffect(() => {
    loadLoyalty();
    loadMembership();
  }, []);

  const loadLoyalty = async () => {
    try {
      const res = await axios.get(`${API}/customer/loyalty`);
      setLoyalty(res.data);
    } catch (error) {
      console.error('Failed to load loyalty:', error);
    }
  };

  const loadMembership = async () => {
    try {
      const res = await axios.get(`${API}/customer/membership-tiers`);
      setMembershipData(res.data);
    } catch (error) {
      console.error('Failed to load membership:', error);
    }
  };

  const handleSubscribe = async (tierId, tierName) => {
    setSubscribing(tierId);
    try {
      await axios.post(`${API}/customer/membership/subscribe/${tierId}`);
      toast.success(`Subscribed to ${tierName}!`);
      loadMembership();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to subscribe');
    } finally { setSubscribing(null); }
  };

  const getDaysLeft = (endDate) => {
    if (!endDate) return 0;
    const diff = new Date(endDate) - new Date();
    return Math.max(0, Math.ceil(diff / 86400000));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      let addressObj = null;
      if (formData.address) {
        try { addressObj = JSON.parse(formData.address); }
        catch { addressObj = { raw: formData.address }; }
      }
      await updateProfile({
        name: formData.name || undefined,
        email: formData.email || undefined,
        address: addressObj
      });
      toast.success('Profile updated!');
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally { setLoading(false); }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    if (!deleteOtp || deleteOtp.length < 4) { toast.error('Enter the OTP'); return; }
    setDeleting(true);
    try {
      await axios.post(`${API}/auth/me/delete`, null, { params: { otp: deleteOtp } });
      toast.success('Account deleted. We are sorry to see you go.');
      logout();
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally { setDeleting(false); }
  };

  const handleRequestDeleteOtp = async () => {
    try {
      await axios.post(`${API}/auth/send-otp`, { phone: user?.phone });
      toast.success('OTP sent for account deletion');
      setShowDeleteDialog(true);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to send OTP'); }
  };

  const handleSwitchRole = async (role) => {
    setSwitchingRole(role);
    try {
      await switchRole(role);
      if (role === 'admin' || role === 'branch_admin') {
        navigate('/admin');
      } else if (role === 'delivery_boy') {
        navigate('/delivery');
      } else {
        navigate('/');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to switch role');
    } finally {
      setSwitchingRole(null);
    }
  };

  // Filter roles: show roles OTHER than current that user can switch to
  const switchableRoles = availableRoles.filter(r => r.role !== 'customer' && r.role !== 'super_admin');

  const currencySymbol = storeSettings?.currency_symbol || '₹';
  const isMembershipEnabled = storeSettings?.tenant_features?.memberships === true;
  const isLoyaltyEnabled = storeSettings?.tenant_features?.loyalty === true;
  const isWalletEnabled = storeSettings?.tenant_features?.customer_wallet === true;

  // ── PROFILE DETAILS SUB-VIEW ──
  if (view === 'details') {
    return (
      <CustomerLayout width="column" title="Profile Details" showBottomNav={false}>
        <div className="p-4 space-y-4">
          {/* Header with Back */}
          <button
            onClick={() => { setView('main'); setEditing(false); }}
            className="flex items-center gap-2 text-[var(--theme-primary)] font-medium text-sm mb-2"
            data-testid="back-to-profile-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Profile
          </button>

          {/* Profile Info Card */}
          <div className="bg-[var(--theme-surface)] rounded-2xl p-5 border border-black/5 text-center">
            <div className="w-16 h-16 rounded-full bg-[var(--theme-primary)]/10 flex items-center justify-center mx-auto mb-3">
              <User className="w-8 h-8 text-[var(--theme-primary)]" />
            </div>
            <h2 className="font-heading text-lg font-bold text-[var(--theme-text-main)]">
              {user?.name || 'User'}
            </h2>
            <p className="text-sm text-[var(--theme-text-muted)] flex items-center justify-center gap-1.5 mt-1">
              <Phone className="w-3.5 h-3.5" /> +91 {user?.phone}
            </p>
            {user?.email && (
              <p className="text-sm text-[var(--theme-text-muted)] flex items-center justify-center gap-1.5 mt-0.5">
                <Mail className="w-3.5 h-3.5" /> {user.email}
              </p>
            )}
          </div>

          {/* Edit Form */}
          <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-[var(--theme-text-main)]">Edit Details</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => editing ? handleSave() : setEditing(true)}
                disabled={loading}
                className="text-[var(--theme-primary)]"
                data-testid="edit-profile-btn"
              >
                {editing ? (
                  <><Save className="w-4 h-4 mr-1" /> Save</>
                ) : (
                  <><Edit2 className="w-4 h-4 mr-1" /> Edit</>
                )}
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-sm font-medium flex items-center gap-2">
                  <User className="w-4 h-4" /> Full Name
                </Label>
                <Input
                  id="name" value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  disabled={!editing} className="mt-1" placeholder="Enter your name"
                  data-testid="name-input"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-sm font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Email
                </Label>
                <Input
                  id="email" type="email" value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  disabled={!editing} className="mt-1" placeholder="Enter your email"
                  data-testid="email-input"
                />
              </div>
              <div>
                <Label htmlFor="address" className="text-sm font-medium flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Delivery Address
                </Label>
                <Textarea
                  id="address" value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  disabled={!editing} className="mt-1" placeholder="Enter your delivery address" rows={3}
                  data-testid="address-input"
                />
              </div>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="bg-[var(--theme-surface)] rounded-2xl border border-red-100 overflow-hidden" data-testid="danger-zone">
            <div className="px-4 py-3 bg-red-50 border-b border-red-100">
              <h3 className="font-bold text-red-700 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Danger Zone
              </h3>
            </div>
            <button
              onClick={handleRequestDeleteOtp}
              className="flex items-center justify-between w-full px-4 py-3.5 hover:bg-red-50/50 transition-colors"
              data-testid="delete-account-btn"
            >
              <div className="flex items-center gap-3">
                <Trash2 className="w-5 h-5 text-red-500" />
                <div className="text-left">
                  <p className="font-medium text-red-600 text-sm">Delete Account</p>
                  <p className="text-xs text-red-400">Permanently remove your account and data</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-red-300" />
            </button>
          </div>
        </div>

        {/* Delete Account Confirmation Dialog */}
        {showDeleteDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteDialog(false)}>
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <h3 className="font-bold text-lg">Delete Account</h3>
              </div>
              <p className="text-gray-500 text-sm mb-4">
                This action is permanent. Your personal data will be removed and cannot be recovered.
              </p>
              <div className="mb-4">
                <Label className="text-sm">Enter OTP sent to your phone</Label>
                <Input
                  value={deleteOtp} onChange={e => setDeleteOtp(e.target.value)}
                  placeholder="Enter OTP" className="mt-1" data-testid="delete-otp-input"
                />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="flex-1">Cancel</Button>
                <Button
                  onClick={handleDeleteAccount} disabled={deleting}
                  className="flex-1 bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete-btn"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </CustomerLayout>
    );
  }

  // ── MAIN PROFILE VIEW ──
  return (
    <CustomerLayout title="Profile">
      {/* Desktop: who you are (profile, wallet, loyalty, membership) on the left,
          links and settings on the right. Phones: the same cards in the same order. */}
      <div className={`p-4 ${TWO_COLUMNS}`}>
        <div className="space-y-4 min-w-0" data-testid="profile-account-column">
        {/* Profile Card */}
        <div className="bg-[var(--theme-surface)] rounded-2xl p-6 border border-black/5 text-center">
          <div className="w-20 h-20 rounded-full bg-[var(--theme-primary)]/10 flex items-center justify-center mx-auto mb-4">
            <User className="w-10 h-10 text-[var(--theme-primary)]" />
          </div>
          <h2 className="font-heading text-xl font-bold text-[var(--theme-text-main)]">
            {user?.name || 'User'}
          </h2>
          <p className="text-[var(--theme-text-muted)] flex items-center justify-center gap-2 mt-1">
            <Phone className="w-4 h-4" /> +91 {user?.phone}
          </p>
          
          {/* Wallet Balance */}
          {isWalletEnabled && (
          <div className="mt-4 p-4 bg-gradient-to-r from-[var(--theme-primary)]/10 to-[var(--theme-secondary)]/10 rounded-xl">
            <div className="flex items-center justify-center gap-2">
              <Wallet className="w-5 h-5 text-[var(--theme-primary)]" />
              <span className="text-sm text-[var(--theme-text-muted)]">Wallet Balance</span>
            </div>
            <p className="font-heading text-2xl font-bold text-[var(--theme-primary)] mt-1">
              {currencySymbol}{(user?.wallet_balance || 0).toFixed(2)}
            </p>
          </div>
          )}
        </div>

        {/* Loyalty Points Card */}
        {isLoyaltyEnabled && loyalty?.loyalty_enabled && (
          <div className="bg-[var(--theme-surface)] rounded-2xl p-5 border border-black/5" data-testid="loyalty-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Gift className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-[var(--theme-text-main)]">Loyalty Points</h3>
                <p className="text-xs text-[var(--theme-text-muted)]">
                  Earn {loyalty.points_per_100} pts per {currencySymbol}100
                </p>
              </div>
            </div>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-3xl font-bold text-amber-600">{loyalty.points_balance || 0}</span>
              <span className="text-sm text-[var(--theme-text-muted)]">points</span>
            </div>
            {loyalty.points_balance > 0 && (
              <p className="text-xs text-[var(--theme-text-muted)]">
                Worth {currencySymbol}{((loyalty.points_balance || 0) * (loyalty.redemption_value || 1)).toFixed(2)} 
                {' '}(min {loyalty.min_redeem_points || 100} pts to redeem)
              </p>
            )}
            {loyalty.transactions?.length > 0 && (
              <div className="mt-4 pt-3 border-t border-black/5">
                <p className="text-xs font-medium text-[var(--theme-text-muted)] mb-2">Recent Activity</p>
                {loyalty.transactions.slice(0, 3).map((t, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1.5">
                    <span className="text-[var(--theme-text-muted)]">{t.description}</span>
                    <span className={`font-semibold ${t.points > 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {t.points > 0 ? '+' : ''}{t.points} pts
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Membership Card */}
        {/* Membership Card — only show if memberships feature is enabled */}
        {isMembershipEnabled && membershipData && (membershipData.current_membership || membershipData.tiers?.length > 0) && (
          <div className="space-y-4" data-testid="membership-section">
            {membershipData.current_membership ? (() => {
              const cm = membershipData.current_membership;
              const daysLeft = getDaysLeft(cm.end_date);
              const isExpiring = daysLeft <= 7;
              const tierIdx = membershipData.tiers?.findIndex(t => t.id === cm.tier_id) ?? 0;
              const colors = TIER_COLORS[Math.max(0, tierIdx) % TIER_COLORS.length];
              const currentTier = membershipData.tiers?.find(t => t.id === cm.tier_id);

              return (
                <div className="bg-[var(--theme-surface)] rounded-2xl border border-black/5 overflow-hidden" data-testid="current-membership-card">
                  <div className={`bg-gradient-to-r ${colors.bg} p-5 text-white`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Crown className="w-6 h-6" />
                        <span className="font-bold text-lg">{cm.tier_name} Member</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${isExpiring ? 'bg-white/30' : 'bg-white/20'}`}>
                        {daysLeft > 0 ? `${daysLeft} days left` : 'Expired'}
                      </span>
                    </div>
                    <p className="text-white/80 text-sm mt-1">{currencySymbol}{cm.monthly_price}/month</p>
                  </div>
                  <div className="p-4 space-y-3">
                    {currentTier && (
                      <div>
                        <p className="text-xs font-medium text-[var(--theme-text-muted)] mb-2">Your Benefits</p>
                        <div className="space-y-1.5">
                          {currentTier.free_delivery && (
                            <div className="flex items-center gap-2 text-sm">
                              <Truck className="w-4 h-4 text-green-500" /> Free delivery on all orders
                            </div>
                          )}
                          {currentTier.extra_loyalty_multiplier > 1 && (
                            <div className="flex items-center gap-2 text-sm">
                              <Star className="w-4 h-4 text-amber-500" /> {currentTier.extra_loyalty_multiplier}x loyalty points
                            </div>
                          )}
                          {(currentTier.benefits || []).map((b, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <Check className="w-4 h-4 text-green-500" /> {b}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2 pt-2">
                      {(isExpiring || daysLeft === 0) && (
                        <Button
                          onClick={() => handleSubscribe(cm.tier_id, cm.tier_name)}
                          disabled={subscribing === cm.tier_id}
                          className="flex-1 bg-[var(--theme-primary)] text-white rounded-xl gap-1"
                          data-testid="renew-membership-btn"
                        >
                          <RefreshCw className="w-4 h-4" />
                          {subscribing === cm.tier_id ? 'Renewing...' : 'Renew'}
                        </Button>
                      )}
                      {membershipData.tiers?.some(t => t.monthly_price > cm.monthly_price) && (
                        <Button
                          variant="outline"
                          onClick={() => document.getElementById('available-tiers')?.scrollIntoView({ behavior: 'smooth' })}
                          className="flex-1 rounded-xl gap-1"
                          data-testid="upgrade-membership-btn"
                        >
                          <ArrowUpRight className="w-4 h-4" /> Upgrade
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })() : (
              <div className="bg-[var(--theme-surface)] rounded-2xl p-5 border border-black/5 text-center">
                <Crown className="w-10 h-10 text-[var(--theme-text-muted)] opacity-40 mx-auto mb-2" />
                <p className="font-bold text-[var(--theme-text-main)]">No Active Membership</p>
                <p className="text-xs text-[var(--theme-text-muted)] mt-1">Subscribe to unlock exclusive benefits</p>
              </div>
            )}

            {membershipData.tiers?.length > 0 && (
              <div id="available-tiers">
                <p className="text-sm font-bold text-[var(--theme-text-main)] mb-3 flex items-center gap-2">
                  <Crown className="w-4 h-4" /> Available Plans
                </p>
                <div className="space-y-3">
                  {membershipData.tiers.map((tier, idx) => {
                    const colors = TIER_COLORS[idx % TIER_COLORS.length];
                    const isCurrent = membershipData.current_membership?.tier_id === tier.id;
                    return (
                      <div key={tier.id} className={`rounded-2xl border-2 overflow-hidden transition-all ${
                        isCurrent ? 'border-[var(--theme-primary)]' : 'border-black/5'
                      }`} data-testid={`tier-option-${tier.id}`}>
                        <div className={`px-4 py-3 flex items-center justify-between ${colors.light}`}>
                          <div className="flex items-center gap-2">
                            <Crown className={`w-5 h-5 ${colors.badge.split(' ')[1]}`} />
                            <span className={`font-bold ${colors.badge.split(' ')[1]}`}>{tier.name}</span>
                            {isCurrent && <span className="text-xs bg-[var(--theme-primary)] text-white px-2 py-0.5 rounded-full">Current</span>}
                          </div>
                          <span className={`font-bold ${colors.badge.split(' ')[1]}`}>{currencySymbol}{tier.monthly_price}/mo</span>
                        </div>
                        <div className="p-4">
                          <div className="space-y-1.5 mb-3">
                            {tier.free_delivery && (
                              <div className="flex items-center gap-2 text-xs text-[var(--theme-text-main)]">
                                <Truck className="w-3.5 h-3.5 text-green-500" /> Free delivery
                              </div>
                            )}
                            {tier.extra_loyalty_multiplier > 1 && (
                              <div className="flex items-center gap-2 text-xs text-[var(--theme-text-main)]">
                                <Star className="w-3.5 h-3.5 text-amber-500" /> {tier.extra_loyalty_multiplier}x loyalty
                              </div>
                            )}
                            {(tier.benefits || []).map((b, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs text-[var(--theme-text-main)]">
                                <Check className="w-3.5 h-3.5 text-green-500" /> {b}
                              </div>
                            ))}
                          </div>
                          {!isCurrent && (
                            <Button
                              onClick={() => handleSubscribe(tier.id, tier.name)}
                              disabled={subscribing === tier.id}
                              size="sm"
                              className="w-full bg-[var(--theme-primary)] text-white rounded-xl"
                              data-testid={`subscribe-tier-${tier.id}`}
                            >
                              {subscribing === tier.id ? 'Subscribing...' :
                                (membershipData.current_membership ? 'Upgrade' : 'Subscribe')}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        </div>

        <div className="space-y-4 min-w-0" data-testid="profile-links-column">
        {/* Quick Links */}
        <div className="bg-[var(--theme-surface)] rounded-2xl border border-black/5 overflow-hidden">
          <button
            onClick={() => setView('details')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            data-testid="profile-details-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[var(--theme-primary)]/10 flex items-center justify-center">
                <User className="w-4 h-4 text-[var(--theme-primary)]" />
              </div>
              <div className="text-left">
                <p className="font-medium text-[var(--theme-text-main)] text-sm">Profile Details</p>
                <p className="text-xs text-[var(--theme-text-muted)]">Edit name, email & manage account</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-[var(--theme-text-muted)]" />
          </button>
          <div className="border-t border-black/5" />
          <button
            onClick={() => navigate('/customer/addresses')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            data-testid="manage-addresses-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-[var(--theme-text-main)] text-sm">Manage Addresses</p>
                <p className="text-xs text-[var(--theme-text-muted)]">Add or edit delivery addresses</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-[var(--theme-text-muted)]" />
          </button>
        </div>

        {/* Store Info */}
        {storeSettings && (
          <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5">
            <h3 className="font-heading font-bold text-[var(--theme-text-main)] mb-2">Registered Store</h3>
            <p className="text-[var(--theme-text-muted)]">{storeSettings.store_name}</p>
          </div>
        )}

        {/* Role Switching — shown when user has admin or delivery_boy roles too */}
        {switchableRoles.length > 0 && (
          <div className="bg-[var(--theme-surface)] rounded-2xl border border-black/5 overflow-hidden" data-testid="role-switch-section">
            <div className="px-4 py-3 border-b border-black/5">
              <h3 className="font-bold text-[var(--theme-text-main)] text-sm flex items-center gap-2">
                <Repeat className="w-4 h-4" /> Switch View
              </h3>
              <p className="text-xs text-[var(--theme-text-muted)] mt-0.5">
                Your account has multiple roles in this store
              </p>
            </div>

            {/* Current role indicator */}
            <div className="px-4 py-3 flex items-center gap-3 bg-[var(--theme-primary)]/5">
              <div className="w-9 h-9 rounded-lg bg-[var(--theme-primary)]/10 flex items-center justify-center">
                <User className="w-4 h-4 text-[var(--theme-primary)]" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[var(--theme-text-main)] text-sm">Customer</p>
                <p className="text-xs text-[var(--theme-text-muted)]">Currently viewing</p>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--theme-primary)]/10 text-[var(--theme-primary)] font-medium">Active</span>
            </div>

            {switchableRoles.map((r) => {
              const isAdmin = r.role === 'admin' || r.role === 'branch_admin';
              const isRider = r.role === 'delivery_boy';
              const icon = isAdmin ? <Store className="w-4 h-4 text-blue-600" /> : isRider ? <Truck className="w-4 h-4 text-orange-600" /> : <UserCog className="w-4 h-4 text-purple-600" />;
              const bgColor = isAdmin ? 'bg-blue-50' : isRider ? 'bg-orange-50' : 'bg-purple-50';
              const label = isAdmin ? 'Store Admin' : isRider ? 'Delivery Partner' : r.role;
              const desc = isAdmin ? 'Manage orders, products & store' : isRider ? 'View & deliver assigned orders' : 'Switch role';

              return (
                <React.Fragment key={r.role}>
                  <div className="border-t border-black/5" />
                  <button
                    onClick={() => handleSwitchRole(r.role)}
                    disabled={switchingRole === r.role}
                    className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    data-testid={`switch-to-${r.role}-btn`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg ${bgColor} flex items-center justify-center`}>
                        {icon}
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-[var(--theme-text-main)] text-sm">{label}</p>
                        <p className="text-xs text-[var(--theme-text-muted)]">{desc}</p>
                      </div>
                    </div>
                    {switchingRole === r.role
                      ? <RefreshCw className="w-4 h-4 text-[var(--theme-text-muted)] animate-spin" />
                      : <ChevronRight className="w-5 h-5 text-[var(--theme-text-muted)]" />
                    }
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Help & Support */}
        <div className="bg-[var(--theme-surface)] rounded-2xl border border-black/5 overflow-hidden" data-testid="help-support-section">
          <div className="px-4 py-3 border-b border-black/5">
            <h3 className="font-bold text-[var(--theme-text-main)] text-sm flex items-center gap-2">
              <Headphones className="w-4 h-4" /> Help & Support
            </h3>
          </div>
          {(storeSettings?.support_phone || storeSettings?.support_whatsapp) ? (
            <>
              {storeSettings.support_whatsapp && (
                <a
                  href={`https://wa.me/${storeSettings.support_whatsapp.replace(/\D/g, '')}?text=Hi, I need help with my order on ${storeSettings.store_name}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-between w-full px-4 py-3.5 hover:bg-green-50/50 transition-colors"
                  data-testid="whatsapp-support-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
                      <MessageCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-[var(--theme-text-main)] text-sm">Chat on WhatsApp</p>
                      <p className="text-xs text-[var(--theme-text-muted)]">{storeSettings.support_whatsapp}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[var(--theme-text-muted)]" />
                </a>
              )}
              {storeSettings.support_whatsapp && storeSettings.support_phone && <div className="border-t border-black/5" />}
              {storeSettings.support_phone && (
                <a
                  href={`tel:${storeSettings.support_phone}`}
                  className="flex items-center justify-between w-full px-4 py-3.5 hover:bg-blue-50/50 transition-colors"
                  data-testid="call-support-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Phone className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-[var(--theme-text-main)] text-sm">Call Support</p>
                      <p className="text-xs text-[var(--theme-text-muted)]">{storeSettings.support_phone}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[var(--theme-text-muted)]" />
                </a>
              )}
            </>
          ) : (
            <div className="px-4 py-3.5 text-sm text-[var(--theme-text-muted)]">
              Contact support at <span className="font-medium">{storeSettings?.store_name || 'the store'}</span> for assistance.
            </div>
          )}
        </div>

        {/* Legal & Logout */}
        <div className="bg-[var(--theme-surface)] rounded-2xl border border-black/5 overflow-hidden">
          {storeSettings?.slug && (
            <>
              <button
                onClick={() => navigate(`/legal/${storeSettings.slug}/customer/privacy`)}
                className="flex items-center justify-between w-full px-4 py-3.5 hover:bg-black/[0.02] transition-colors"
                data-testid="privacy-policy-btn"
              >
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-blue-500" />
                  <span className="font-medium text-[var(--theme-text-main)]">Privacy Policy</span>
                </div>
                <ChevronRight className="w-4 h-4 text-[var(--theme-text-muted)]" />
              </button>
              <div className="border-t border-black/5" />
              <button
                onClick={() => navigate(`/legal/${storeSettings.slug}/customer/terms`)}
                className="flex items-center justify-between w-full px-4 py-3.5 hover:bg-black/[0.02] transition-colors"
                data-testid="terms-btn"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-green-500" />
                  <span className="font-medium text-[var(--theme-text-main)]">Terms & Conditions</span>
                </div>
                <ChevronRight className="w-4 h-4 text-[var(--theme-text-muted)]" />
              </button>
              <div className="border-t border-black/5" />
            </>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center justify-between w-full px-4 py-3.5 hover:bg-red-50/50 transition-colors"
            data-testid="logout-btn"
          >
            <div className="flex items-center gap-3">
              <LogOut className="w-5 h-5 text-red-500" />
              <span className="font-medium text-red-600">Logout</span>
            </div>
          </button>
        </div>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default CustomerProfile;
